"use client";

import { c as _c } from "react/compiler-runtime";
import MediaTypeBadge from "@/components/drops/media/MediaTypeBadge";
import MediaDisplay from "@/components/drops/view/item/content/media/MediaDisplay";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import { useVotingModalState } from "@/components/voting/useVotingModalState";
import WinnerDropBadge from "@/components/waves/drops/winner/WinnerDropBadge";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { ImageScale } from "@/helpers/image.helpers";
import { ExtendedDrop, getDropPreviewImageUrl } from "@/helpers/waves/drop.helpers";
import { WAVE_VOTE_STATS_LABELS, WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import { startDropOpen } from "@/utils/monitoring/dropOpenTiming";
import Link from "next/link";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { WaveLeaderboardIdentity } from "../identity/WaveLeaderboardIdentity";
import WaveLeaderboardGalleryItemVotes from "./WaveLeaderboardGalleryItemVotes";
import ApprovalStatusBadge from "@/components/waves/approval/ApprovalStatusBadge";
import { isOfficiallyApprovedDrop } from "@/helpers/waves/approve-wave.helpers";
interface WaveLeaderboardGalleryItemProps {
  readonly drop: ExtendedDrop;
  readonly onDropClick: (drop: ExtendedDrop) => void;
  readonly artFocused?: boolean | undefined;
  readonly activeSort?: WaveDropsLeaderboardSort | undefined;
  readonly animationKey?: number | undefined;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
}
const getVoteStyle = (isNegative: boolean, isZero: boolean, artFocused: boolean) => {
  if (isZero || artFocused && isNegative) {
    return "tw-text-iron-400";
  }
  if (artFocused) {
    return "tw-text-iron-300";
  }
  return isNegative ? "tw-text-rose-500" : "tw-text-emerald-500";
};
interface LeaderboardResultBadgeProps {
  readonly drop: ExtendedDrop;
  readonly isApproveDrop: boolean;
}
function LeaderboardResultBadge(t0) {
  const $ = _c(5);
  const {
    drop,
    isApproveDrop
  } = t0;
  if (isApproveDrop) {
    let t1;
    if ($[0] !== drop) {
      t1 = isOfficiallyApprovedDrop(drop) ? <ApprovalStatusBadge approvedAt={drop.winning_context?.decision_time ?? null} order={drop.winning_context?.place ?? drop.rank} /> : null;
      $[0] = drop;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const t1 = drop.winning_context?.decision_time ?? null;
  let t2;
  if ($[2] !== drop.rank || $[3] !== t1) {
    t2 = <WinnerDropBadge rank={drop.rank} decisionTime={t1} />;
    $[2] = drop.rank;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
export const WaveLeaderboardGalleryItem = memo<WaveLeaderboardGalleryItemProps>(t0 => {
  const $ = _c(94);
  const {
    drop,
    onDropClick,
    artFocused: t1,
    activeSort,
    animationKey: t2,
    isVotingClosed: t3,
    isVotingControlsLocked: t4,
    winningThreshold
  } = t0;
  const artFocused = t1 === undefined ? true : t1;
  const animationKey = t2 === undefined ? 0 : t2;
  const isVotingClosed = t3 === undefined ? false : t3;
  const isVotingControlsLocked = t4 === undefined ? false : t4;
  const isVotingActionLocked = isVotingClosed || isVotingControlsLocked;
  const {
    isOpen: isVoteModalOpen,
    open: openVoteModal,
    close: closeVoteModal
  } = useVotingModalState(isVotingActionLocked);
  const [isHighlighting, setIsHighlighting] = useState(false);
  const isMobileScreen = useIsMobileScreen();
  const isTabletOrSmaller = useMediaQuery("(max-width: 1023px)");
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const {
    canShowVote
  } = useDropInteractionRules(drop);
  const canShowVotingAction = canShowVote && !isVotingActionLocked;
  const primaryMedia = drop.parts[0]?.media[0];
  let t5;
  if ($[0] !== primaryMedia?.mime_type) {
    t5 = primaryMedia?.mime_type.startsWith("video/");
    $[0] = primaryMedia?.mime_type;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const isPrimaryMediaVideo = t5;
  const mediaImageScale = isTabletOrSmaller ? ImageScale.AUTOx450 : ImageScale.AUTOx1080;
  let t6;
  if ($[2] !== drop.metadata || $[3] !== isPrimaryMediaVideo) {
    t6 = isPrimaryMediaVideo ? null : getDropPreviewImageUrl(drop.metadata);
    $[2] = drop.metadata;
    $[3] = isPrimaryMediaVideo;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const previewImageUrl = t6;
  const isFirstRenderRef = useRef(true);
  const previousSortRef = useRef(activeSort);
  const timerRef = useRef(null);
  let t7;
  let t8;
  if ($[5] !== activeSort || $[6] !== animationKey || $[7] !== hasTouchScreen) {
    t7 = () => {
      if (hasTouchScreen) {
        return;
      }
      const sortChanged = previousSortRef.current !== activeSort;
      const shouldAnimate = isFirstRenderRef.current && animationKey > 0 || !isFirstRenderRef.current && sortChanged;
      isFirstRenderRef.current = false;
      previousSortRef.current = activeSort;
      if (!shouldAnimate) {
        return;
      }
      setIsHighlighting(true);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        setIsHighlighting(false);
      }, 700);
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    };
    t8 = [activeSort, animationKey, hasTouchScreen];
    $[5] = activeSort;
    $[6] = animationKey;
    $[7] = hasTouchScreen;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  const hasUserVoted = drop.context_profile_context?.rating !== undefined;
  const userVote = drop.context_profile_context?.rating ?? 0;
  const isNegativeVote = userVote < 0;
  const isZeroVote = userVote === 0;
  let t9;
  if ($[10] !== artFocused || $[11] !== isNegativeVote || $[12] !== isZeroVote) {
    t9 = getVoteStyle(isNegativeVote, isZeroVote, artFocused);
    $[10] = artFocused;
    $[11] = isNegativeVote;
    $[12] = isZeroVote;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const voteStyle = t9;
  const votingCreditType = drop.wave.voting_credit_type;
  const votingCreditLabel = WAVE_VOTING_LABELS[votingCreditType] ?? votingCreditType;
  const isApproveDrop = typeof winningThreshold === "number" && winningThreshold > 0;
  let t10;
  if ($[14] !== drop || $[15] !== isMobileScreen || $[16] !== onDropClick) {
    t10 = () => {
      startDropOpen({
        dropId: drop.id,
        waveId: drop.wave.id,
        source: "leaderboard_grid",
        isMobile: isMobileScreen
      });
      onDropClick(drop);
    };
    $[14] = drop;
    $[15] = isMobileScreen;
    $[16] = onDropClick;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const handleImageClick = t10;
  let t11;
  if ($[18] !== openVoteModal) {
    t11 = () => {
      openVoteModal();
    };
    $[18] = openVoteModal;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const handleVoteButtonClick = t11;
  const transitionClasses = hasTouchScreen ? "" : "tw-transition-all tw-duration-300 tw-ease-out";
  const groupClasses = artFocused ? `tw-group ${transitionClasses}` : "";
  const containerClass = `${groupClasses} tw-relative tw-w-full tw-min-w-0 tw-bg-iron-950/50 tw-border tw-border-solid tw-border-iron-800 tw-rounded-lg desktop-hover:hover:tw-border-iron-700 tw-shadow-lg desktop-hover:hover:tw-shadow-xl`;
  const highlightAnimation = isHighlighting && !hasTouchScreen ? "tw-animate-gallery-reveal" : "";
  const imageScaleClasses = hasTouchScreen ? "" : `tw-transform tw-duration-700 tw-ease-out group-hover/image:tw-scale-105 ${highlightAnimation}`;
  const t12 = `tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center ${imageScaleClasses}`;
  const t13 = primaryMedia?.mime_type ?? "image/jpeg";
  const t14 = primaryMedia?.url ?? "";
  let t15;
  if ($[20] !== mediaImageScale || $[21] !== previewImageUrl || $[22] !== t13 || $[23] !== t14) {
    t15 = <MediaDisplay media_mime_type={t13} media_url={t14} disableMediaInteraction={true} imageScale={mediaImageScale} previewImageUrl={previewImageUrl} />;
    $[20] = mediaImageScale;
    $[21] = previewImageUrl;
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t12 || $[26] !== t15) {
    t16 = <div className={t12}>{t15}</div>;
    $[25] = t12;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== handleImageClick || $[29] !== t16) {
    t17 = <button className="tw-aspect-square tw-relative tw-cursor-pointer tw-touch-pan-y tw-overflow-hidden tw-bg-iron-900 tw-group/image tw-m-0 tw-w-full tw-overflow-hidden tw-rounded-lg tw-border-none tw-bg-transparent tw-p-0 tw-text-left" onClick={handleImageClick} type="button">{t16}</button>;
    $[28] = handleImageClick;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const t18 = primaryMedia?.mime_type;
  let t19;
  if ($[31] !== drop.id || $[32] !== t18) {
    t19 = <MediaTypeBadge mimeType={t18} dropId={drop.id} size="sm" />;
    $[31] = drop.id;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== drop.title) {
    t20 = drop.title && <h3 className="tw-mb-0 tw-truncate tw-text-sm tw-font-bold tw-leading-tight tw-text-iron-200">{drop.title}</h3>;
    $[34] = drop.title;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t19 || $[37] !== t20) {
    t21 = <div className="tw-flex tw-items-center tw-gap-1.5">{t19}{t20}</div>;
    $[36] = t19;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== drop.author) {
    t22 = drop.author?.handle && <UserProfileTooltipWrapper user={drop.author.handle ?? drop.author.id}><Link onClick={_temp} href={`/${drop.author?.handle}`} className="tw-mt-0.5 tw-block tw-max-w-full tw-truncate tw-text-xs tw-text-iron-400 tw-no-underline tw-transition-colors tw-duration-150 desktop-hover:hover:tw-text-iron-300 desktop-hover:hover:tw-underline">{drop.author?.handle}</Link></UserProfileTooltipWrapper>;
    $[39] = drop.author;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t21 || $[42] !== t22) {
    t23 = <div className="tw-mr-2 tw-min-w-0 tw-flex-1">{t21}{t22}</div>;
    $[41] = t21;
    $[42] = t22;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== drop || $[45] !== isApproveDrop) {
    t24 = <LeaderboardResultBadge drop={drop} isApproveDrop={isApproveDrop} />;
    $[44] = drop;
    $[45] = isApproveDrop;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  let t25;
  if ($[47] !== t23 || $[48] !== t24) {
    t25 = <div className="tw-mb-3 tw-flex tw-min-w-0 tw-items-start tw-justify-between tw-gap-2">{t23}{t24}</div>;
    $[47] = t23;
    $[48] = t24;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== drop) {
    t26 = <WaveLeaderboardIdentity drop={drop} variant="condensed" className="tw-mb-3" />;
    $[50] = drop;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  const t27 = artFocused ? "subtle" : "default";
  let t28;
  if ($[52] !== drop || $[53] !== isVotingClosed || $[54] !== t27 || $[55] !== winningThreshold) {
    t28 = <div className="tw-min-w-0 tw-flex-1"><WaveLeaderboardGalleryItemVotes drop={drop} variant={t27} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} /></div>;
    $[52] = drop;
    $[53] = isVotingClosed;
    $[54] = t27;
    $[55] = winningThreshold;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  let t29;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true" className="tw-size-3 tw-flex-shrink-0"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" /></svg>;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== drop.raters_count) {
    t30 = formatNumberWithCommas(drop.raters_count);
    $[58] = drop.raters_count;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== t30) {
    t31 = <div className="tw-ml-auto tw-flex tw-flex-shrink-0 tw-items-center tw-gap-1 tw-text-iron-500">{t29}<span className="tw-font-medium">{t30}</span></div>;
    $[60] = t30;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  let t32;
  if ($[62] !== t28 || $[63] !== t31) {
    t32 = <div className="tw-mb-3 tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-justify-between tw-gap-y-2 tw-text-xs">{t28}{t31}</div>;
    $[62] = t28;
    $[63] = t31;
    $[64] = t32;
  } else {
    t32 = $[64];
  }
  let t33;
  if ($[65] !== hasUserVoted || $[66] !== isNegativeVote || $[67] !== userVote || $[68] !== voteStyle || $[69] !== votingCreditLabel) {
    t33 = hasUserVoted && <span className="tw-min-w-0 tw-break-words tw-font-mono tw-text-[11px] tw-text-iron-500">{WAVE_VOTE_STATS_LABELS.YOUR_VOTES}:{" "}<span className={voteStyle}>{isNegativeVote && "-"}{formatNumberWithCommas(Math.abs(userVote))}{" "}{votingCreditLabel}</span></span>;
    $[65] = hasUserVoted;
    $[66] = isNegativeVote;
    $[67] = userVote;
    $[68] = voteStyle;
    $[69] = votingCreditLabel;
    $[70] = t33;
  } else {
    t33 = $[70];
  }
  let t34;
  if ($[71] !== artFocused || $[72] !== canShowVotingAction || $[73] !== drop || $[74] !== handleVoteButtonClick) {
    t34 = canShowVotingAction && <div className="tw-ml-auto tw-flex tw-min-w-0 tw-flex-1 tw-justify-end"><VotingModalButton drop={drop} onClick={handleVoteButtonClick} variant={artFocused ? "subtle" : "default"} /></div>;
    $[71] = artFocused;
    $[72] = canShowVotingAction;
    $[73] = drop;
    $[74] = handleVoteButtonClick;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] !== t33 || $[77] !== t34) {
    t35 = <div className="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-3 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800/50 tw-pt-2">{t33}{t34}</div>;
    $[76] = t33;
    $[77] = t34;
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  let t36;
  if ($[79] !== t25 || $[80] !== t26 || $[81] !== t32 || $[82] !== t35) {
    t36 = <div className="tw-rounded-b-lg tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800 tw-bg-iron-950/50 tw-p-3">{t25}{t26}{t32}{t35}</div>;
    $[79] = t25;
    $[80] = t26;
    $[81] = t32;
    $[82] = t35;
    $[83] = t36;
  } else {
    t36 = $[83];
  }
  let t37;
  if ($[84] !== closeVoteModal || $[85] !== drop || $[86] !== isMobileScreen || $[87] !== isVoteModalOpen) {
    t37 = isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVoteModalOpen} onClose={closeVoteModal} /> : <VotingModal drop={drop} isOpen={isVoteModalOpen} onClose={closeVoteModal} />;
    $[84] = closeVoteModal;
    $[85] = drop;
    $[86] = isMobileScreen;
    $[87] = isVoteModalOpen;
    $[88] = t37;
  } else {
    t37 = $[88];
  }
  let t38;
  if ($[89] !== containerClass || $[90] !== t17 || $[91] !== t36 || $[92] !== t37) {
    t38 = <div className={containerClass}>{t17}{t36}{t37}</div>;
    $[89] = containerClass;
    $[90] = t17;
    $[91] = t36;
    $[92] = t37;
    $[93] = t38;
  } else {
    t38 = $[93];
  }
  return t38;
});
WaveLeaderboardGalleryItem.displayName = "WaveLeaderboardGalleryItem";
function _temp(e) {
  return e.stopPropagation();
}